import React, { useState, useEffect } from 'react';
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  Typography,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Menu,
  MenuItem,
  Alert,
  CircularProgress,
  InputAdornment,
  Fab,
  Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  MoreVert as MoreVertIcon,
  Search as SearchIcon,
  PushPin as PushPinIcon,
  Archive as ArchiveIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Chat as ChatIcon,
  Clear as ClearIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';
import { conversationsAPI } from '../services/api';
import { Conversation } from '../types';

interface ConversationListProps {
  currentUserId: string;
  selectedConversationId?: string;
  onConversationSelect: (conversationId: string) => void;
  onConversationCreate: (conversation: Conversation) => void;
}

const ConversationList: React.FC<ConversationListProps> = ({
  currentUserId,
  selectedConversationId,
  onConversationSelect,
  onConversationCreate
}) => {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // UI state
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  
  // Create/Edit form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    conversation_type: 'wardrobe_chat'
  });

  useEffect(() => {
    loadConversations();
  }, [currentUserId]); // loadConversations is stable

  const loadConversations = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await conversationsAPI.getConversations({
        status: 'active',
        limit: 100
      });

      setConversations(response.data.conversations);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load conversations');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateConversation = async () => {
    try {
      const response = await conversationsAPI.createConversation({
        title: formData.title || `Chat - ${new Date().toLocaleDateString()}`,
        description: formData.description,
        conversation_type: formData.conversation_type
      });

      const newConversation = response.data;
      setConversations(prev => [newConversation, ...prev]);
      setShowCreateDialog(false);
      setFormData({ title: '', description: '', conversation_type: 'wardrobe_chat' });

      // Call onConversationCreate first to update parent state
      onConversationCreate(newConversation);

      // Small delay to ensure state updates propagate before loading conversation
      // This prevents race condition where conversation is not found immediately
      setTimeout(() => {
        onConversationSelect(newConversation.id);
      }, 100);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to create conversation');
    }
  };

  const handleEditConversation = async () => {
    if (!selectedConversation) return;

    try {
      const response = await conversationsAPI.updateConversation(selectedConversation.id, {
        title: formData.title,
        description: formData.description
      });

      setConversations(prev =>
        prev.map(conv =>
          conv.id === selectedConversation.id ? response.data : conv
        )
      );

      setShowEditDialog(false);
      setSelectedConversation(null);
      setFormData({ title: '', description: '', conversation_type: 'wardrobe_chat' });
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update conversation');
    }
  };

  const handleConversationAction = async (action: string, conversation: Conversation) => {
    setAnchorEl(null);
    setSelectedConversation(conversation);

    switch (action) {
      case 'pin':
        try {
          await conversationsAPI.updateConversation(conversation.id, {
            is_pinned: !conversation.is_pinned
          });
          setConversations(prev =>
            prev.map(conv =>
              conv.id === conversation.id
                ? { ...conv, is_pinned: !conv.is_pinned }
                : conv
            )
          );
        } catch (err: any) {
          setError(err.response?.data?.error || 'Failed to update conversation');
        }
        break;

      case 'edit':
        setFormData({
          title: conversation.title,
          description: conversation.description || '',
          conversation_type: conversation.conversation_type
        });
        setShowEditDialog(true);
        break;

      case 'archive':
        try {
          await conversationsAPI.updateConversation(conversation.id, {
            status: 'archived'
          });
          setConversations(prev => prev.filter(conv => conv.id !== conversation.id));
        } catch (err: any) {
          setError(err.response?.data?.error || 'Failed to archive conversation');
        }
        break;

      case 'delete':
        try {
          await conversationsAPI.deleteConversation(conversation.id, false);
          setConversations(prev => prev.filter(conv => conv.id !== conversation.id));
        } catch (err: any) {
          setError(err.response?.data?.error || 'Failed to delete conversation');
        }
        break;
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      loadConversations();
      return;
    }

    try {
      const response = await conversationsAPI.searchConversations({
        query: searchQuery,
        limit: 50
      });

      // Combine conversations and messages results
      const foundConversations = response.data.conversations;
      
      // Add conversations that have matching messages
      const messageConversationIds = response.data.messages.map((msg: any) => msg.conversation_id);
      const additionalConversations = conversations.filter(conv =>
        messageConversationIds.includes(conv.id) && 
        !foundConversations.find((fc: Conversation) => fc.id === conv.id)
      );

      setConversations([...foundConversations, ...additionalConversations]);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Search failed');
    }
  };

  const clearSearch = () => {
    setSearchQuery('');
    loadConversations();
  };

  const getConversationIcon = (type: string) => {
    switch (type) {
      case 'styling_session':
        return '✨';
      case 'outfit_planning':
        return '👗';
      case 'wardrobe_organization':
        return '🗂️';
      default:
        return '💬';
    }
  };

  const getConversationTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      wardrobe_chat: 'Chat',
      styling_session: 'Styling',
      outfit_planning: 'Planning',
      wardrobe_organization: 'Organization'
    };
    return labels[type] || 'Chat';
  };

  const filteredConversations = conversations.sort((a, b) => {
    // Sort by pinned first, then by last activity
    if (a.is_pinned && !b.is_pinned) return -1;
    if (!a.is_pinned && b.is_pinned) return 1;
    
    const aTime = new Date(a.last_message_at || a.updated_at).getTime();
    const bTime = new Date(b.last_message_at || b.updated_at).getTime();
    return bTime - aTime;
  });

  const renderCreateEditDialog = (isEdit: boolean = false) => (
    <Dialog 
      open={isEdit ? showEditDialog : showCreateDialog} 
      onClose={() => {
        setShowCreateDialog(false);
        setShowEditDialog(false);
        setFormData({ title: '', description: '', conversation_type: 'wardrobe_chat' });
      }}
      maxWidth="sm" 
      fullWidth
    >
      <DialogTitle>
        {isEdit ? 'Edit Conversation' : 'New Conversation'}
      </DialogTitle>
      <DialogContent>
        <TextField
          fullWidth
          autoFocus
          label="Title"
          value={formData.title}
          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              isEdit ? handleEditConversation() : handleCreateConversation();
            }
          }}
          placeholder="Give your conversation a name..."
          sx={{ mb: 2, mt: 1 }}
        />
        
        <TextField
          fullWidth
          label="Description"
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          placeholder="What would you like to discuss?"
          multiline
          rows={3}
          sx={{ mb: 2 }}
        />

        {!isEdit && (
          <TextField
            select
            fullWidth
            label="Type"
            value={formData.conversation_type}
            onChange={(e) => setFormData({ ...formData, conversation_type: e.target.value })}
            SelectProps={{ native: true }}
          >
            <option value="wardrobe_chat">General Chat</option>
            <option value="styling_session">Styling Session</option>
            <option value="outfit_planning">Outfit Planning</option>
            <option value="wardrobe_organization">Organization</option>
          </TextField>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => {
          setShowCreateDialog(false);
          setShowEditDialog(false);
        }}>
          Cancel
        </Button>
        <Button 
          variant="contained"
          onClick={isEdit ? handleEditConversation : handleCreateConversation}
        >
          {isEdit ? 'Update' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Header with Search */}
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">Conversations</Typography>
          <Box>
            <Tooltip title="New conversation">
              <IconButton onClick={() => setShowCreateDialog(true)} size="small" color="primary">
                <AddIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Refresh">
              <IconButton onClick={loadConversations} size="small">
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        <TextField
          fullWidth
          size="small"
          placeholder="Search conversations..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: searchQuery && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={clearSearch}>
                  <ClearIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* Conversation List */}
      <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
        {loading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : filteredConversations.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <ChatIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="textSecondary" gutterBottom>
              {searchQuery ? 'No conversations found' : 'No conversations yet'}
            </Typography>
            <Typography variant="body2" color="textSecondary" mb={3}>
              {searchQuery 
                ? 'Try a different search term'
                : 'Start a conversation to get personalized wardrobe advice!'
              }
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setShowCreateDialog(true)}
            >
              Start Chatting
            </Button>
          </Box>
        ) : (
          <List sx={{ py: 0 }}>
            {filteredConversations.map((conversation) => (
              <ListItem key={conversation.id} disablePadding>
                <ListItemButton
                  selected={conversation.id === selectedConversationId}
                  onClick={() => onConversationSelect(conversation.id)}
                  sx={{
                    borderLeft: conversation.is_pinned ? 3 : 0,
                    borderColor: 'primary.main',
                    '&.Mui-selected': {
                      backgroundColor: 'action.selected',
                    }
                  }}
                >
                  <Box sx={{ width: '100%' }}>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                        <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                          <Typography component="span">
                            {getConversationIcon(conversation.conversation_type)}
                          </Typography>
                          <Typography
                            variant="subtitle1"
                            noWrap
                            sx={{
                              fontWeight: conversation.is_pinned ? 'bold' : 'normal'
                            }}
                          >
                            {conversation.title}
                          </Typography>
                          {conversation.is_pinned && (
                            <PushPinIcon fontSize="small" color="primary" />
                          )}
                        </Box>

                        {conversation.description && (
                          <Typography
                            variant="body2"
                            color="textSecondary"
                            noWrap
                            sx={{ mb: 0.5 }}
                          >
                            {conversation.description}
                          </Typography>
                        )}

                        <Box display="flex" alignItems="center" gap={1}>
                          <Chip
                            label={getConversationTypeLabel(conversation.conversation_type)}
                            size="small"
                            variant="outlined"
                            sx={{ height: 20, fontSize: '0.7rem' }}
                          />
                          <Typography variant="caption" color="textSecondary">
                            {conversation.message_count} messages
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            •
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {formatDistanceToNow(
                              new Date(conversation.last_message_at || conversation.updated_at),
                              { addSuffix: true }
                            )}
                          </Typography>
                        </Box>
                      </Box>

                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          setAnchorEl(e.currentTarget);
                          setSelectedConversation(conversation);
                        }}
                        sx={{ opacity: 0.7, '&:hover': { opacity: 1 } }}
                      >
                        <MoreVertIcon />
                      </IconButton>
                    </Box>
                  </Box>
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        )}
      </Box>

      {/* Floating Action Button */}
      <Tooltip title="Start New Conversation">
        <Fab
          color="primary"
          onClick={() => setShowCreateDialog(true)}
          sx={{
            position: 'fixed',
            bottom: 16,
            right: 16
          }}
        >
          <AddIcon />
        </Fab>
      </Tooltip>

      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
      >
        <MenuItem onClick={() => handleConversationAction('pin', selectedConversation!)}>
          <PushPinIcon sx={{ mr: 1 }} fontSize="small" />
          {selectedConversation?.is_pinned ? 'Unpin' : 'Pin'}
        </MenuItem>
        <MenuItem onClick={() => handleConversationAction('edit', selectedConversation!)}>
          <EditIcon sx={{ mr: 1 }} fontSize="small" />
          Edit
        </MenuItem>
        <MenuItem onClick={() => handleConversationAction('archive', selectedConversation!)}>
          <ArchiveIcon sx={{ mr: 1 }} fontSize="small" />
          Archive
        </MenuItem>
        <MenuItem onClick={() => handleConversationAction('delete', selectedConversation!)}>
          <DeleteIcon sx={{ mr: 1 }} fontSize="small" />
          Delete
        </MenuItem>
      </Menu>

      {/* Create Dialog */}
      {renderCreateEditDialog(false)}
      
      {/* Edit Dialog */}
      {renderCreateEditDialog(true)}
    </Box>
  );
};

export default ConversationList;